<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { FormKit } from '@formkit/vue';
import { useBranding } from 'shared/composables/useBranding';

import NextButton from 'dashboard/components-next/button/Button.vue';
import VtigerModuleMapping from './VtigerModuleMapping.vue';

export default {
  components: {
    FormKit,
    NextButton,
    VtigerModuleMapping,
  },
  props: {
    integrationId: {
      type: String,
      required: true,
    },
    hook: {
      type: Object,
      default: null,
    },
  },
  emits: ['close'],
  setup(props) {
    const { integration, isHookTypeInbox } = useIntegrationHook(
      props.integrationId
    );
    const { replaceInstallationName } = useBranding();

    return { integration, isHookTypeInbox, replaceInstallationName };
  },
  data() {
    return {
      endPoint: '',
      alertMessage: '',
      activeTab: 'connection',
      values: this.hook ? { ...this.hook.settings } : {},
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
      dialogFlowEnabledInboxes: 'inboxes/dialogFlowEnabledInboxes',
    }),
    inboxes() {
      return this.dialogFlowEnabledInboxes
        .filter(inbox => {
          if (!this.isIntegrationDialogflow) {
            return true;
          }
          return !this.connectedDialogflowInboxIds.includes(inbox.id);
        })
        .map(inbox => ({ label: inbox.name, value: inbox.id }));
    },

    connectedDialogflowInboxIds() {
      if (!this.isIntegrationDialogflow) {
        return [];
      }
      return this.integration.hooks.map(hook => hook.inbox?.id);
    },
    formItems() {
      return this.integration.settings_form_schema.filter(item => {
        return !(this.isVtiger && this.isEditing && ['module', 'assignment_field'].includes(item.name));
      }).map(item => {
        if (!this.isEditing || item.name !== 'access_key') {
          return item;
        }

        return {
          ...item,
          validation: '',
          help: this.$t('INTEGRATION_APPS.VTIGER.ACCESS_KEY_EDIT_HELP'),
        };
      });
    },
    isEditing() {
      return Boolean(this.hook?.id);
    },
    isSubmitting() {
      return this.uiFlags.isCreatingHook || this.uiFlags.isUpdating;
    },
    isIntegrationDialogflow() {
      return this.integration.id === 'dialogflow';
    },
    isVtiger() {
      return this.integration.id === 'vtiger';
    },
    showVtigerMappingTab() {
      return this.isVtiger && this.isEditing;
    },
    submitButtonLabel() {
      if (this.integration.id === 'openai' && this.uiFlags.isCreatingHook) {
        return this.$t('INTEGRATION_APPS.ADD.FORM.VALIDATING_OPENAI');
      }
      if (this.isEditing) {
        return this.$t('INTEGRATION_APPS.UPDATE.FORM.SUBMIT');
      }

      return this.$t('INTEGRATION_APPS.ADD.FORM.SUBMIT');
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    onVtigerMappingSaved(settings) {
      this.values = { ...settings };
    },
    buildHookPayload() {
      const hookPayload = {
        app_id: this.integration.id,
        settings: {},
      };

      hookPayload.settings = Object.keys(this.values).reduce((acc, key) => {
        if (key !== 'inbox') {
          acc[key] = this.values[key];
        }
        return acc;
      }, {});

      this.formItems.forEach(item => {
        if (item.validation?.includes('JSON')) {
          hookPayload.settings[item.name] = JSON.parse(
            hookPayload.settings[item.name]
          );
        }
      });

      if (this.isHookTypeInbox && this.values.inbox) {
        hookPayload.inbox_id = this.values.inbox;
      }

      return hookPayload;
    },
    async submitForm() {
      try {
        const hookPayload = this.buildHookPayload();
        if (this.isEditing) {
          await this.$store.dispatch('integrations/updateHook', {
            hookId: this.hook.id,
            hookData: { settings: hookPayload.settings },
          });
        } else {
          await this.$store.dispatch('integrations/createHook', hookPayload);
        }
        this.alertMessage = this.isEditing
          ? this.$t('INTEGRATION_APPS.UPDATE.API.SUCCESS_MESSAGE')
          : this.$t('INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage || this.$t('INTEGRATION_APPS.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto integration-hooks">
    <woot-modal-header
      :header-title="integration.name"
      :header-content="replaceInstallationName(integration.short_description)"
    />
    <div v-if="showVtigerMappingTab" class="flex gap-2 pb-4">
      <NextButton
        :variant="activeTab === 'connection' ? 'solid' : 'faded'"
        color="slate"
        type="button"
        :label="$t('INTEGRATION_APPS.VTIGER.TABS.CONNECTION')"
        @click="setActiveTab('connection')"
      />
      <NextButton
        :variant="activeTab === 'mapping' ? 'solid' : 'faded'"
        color="slate"
        type="button"
        :label="$t('INTEGRATION_APPS.VTIGER.TABS.MAPPING')"
        @click="setActiveTab('mapping')"
      />
    </div>
    <FormKit
      v-if="activeTab === 'connection'"
      v-model="values"
      type="form"
      form-class="w-full grid gap-4"
      :submit-attrs="{
        inputClass: 'hidden',
        wrapperClass: 'hidden',
      }"
      :incomplete-message="false"
      @submit="submitForm"
    >
      <template v-for="item in formItems" :key="item.name">
        <FormKit v-bind="item" />
        <div
          v-if="isVtiger && isEditing && item.name === 'access_key' && hook.access_key_hint"
          class="-mt-2 flex items-center justify-between rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm"
        >
          <span class="text-n-slate-11">
            {{ $t('INTEGRATION_APPS.VTIGER.CURRENT_ACCESS_KEY') }}
          </span>
          <code class="font-medium text-n-slate-12">
            {{ hook.access_key_hint }}
          </code>
        </div>
      </template>
      <FormKit
        v-if="isHookTypeInbox"
        :options="inboxes"
        type="select"
        name="inbox"
        input-class="reset-base"
        :placeholder="$t('INTEGRATION_APPS.ADD.FORM.INBOX.LABEL')"
        :label="$t('INTEGRATION_APPS.ADD.FORM.INBOX.PLACEHOLDER')"
        validation="required"
        validation-name="Inbox"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="submitButtonLabel"
          :is-loading="isSubmitting"
        />
      </div>
    </FormKit>
    <VtigerModuleMapping
      v-else-if="showVtigerMappingTab"
      :hook="hook"
      @saved="onVtigerMappingSaved"
    />
  </div>
</template>

<style lang="css">
.formkit-outer {
  @apply mt-2;
}

.formkit-form > .formkit-wrapper > ul.formkit-messages {
  @apply hidden;
}

.formkit-form .formkit-help {
  @apply text-n-slate-10 text-sm font-normal mt-2 w-full;
}

/* equivalent of .reset-base */
.formkit-input {
  margin-bottom: 0px !important;
}

[data-invalid] .formkit-message {
  @apply text-n-ruby-9 block text-xs font-normal my-1 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-center gap-2 px-0.5;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}

.formkit-actions {
  @apply hidden;
}
</style>
